

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    CircularProgress
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewItineraryModal from 'components/NewItineraryModal';

const Itineraries = () => {
    const [itineraries, setItineraries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('itineraries');

    useEffect(() => {
        fetchItineraries();
    }, []);

    const fetchItineraries = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/itineraries/');
            setItineraries(response.data);
        } catch (error) {
            console.error('Error fetching itineraries:', error);
            setSnackbar({ open: true, message: 'Error loading itineraries', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveItinerary = async (payload) => {
        try {
            await axiosInstance.post('/itineraries/', payload);
            setSnackbar({ open: true, message: 'Itinerary created successfully', severity: 'success' });
            fetchItineraries();
        } catch (error) {
            console.error('Error saving itinerary:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/itineraries/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Name",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Trip",
            accessor: "trip_id",
            width: "15%",
            Cell: ({ row }) => row.original.trip?.name || row.original.trip_id
        },
        {
            Header: "Total Days",
            accessor: "total_days",
            width: "10%"
        },
        {
            Header: "Title",
            accessor: "title",
            width: "15%"
        },
        {
            Header: "Type",
            accessor: "type",
            width: "10%"
        },
        {
            Header: "Trip Option",
            accessor: "trip_option_id",
            width: "15%",
            Cell: ({ row }) => row.original.trip_option?.name || row.original.trip_option_id
        },
        {
            Header: "Location",
            accessor: "location",
            width: "10%"
        },
        {
            Header: "Created At",
            accessor: "created_at",
            width: "10%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: itineraries,
    }), [itineraries]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Itineraries</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Itinerary
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : itineraries.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                        onRowClick={handleRowClick}
                                    />
                                ) : (
                                    <EmptyState moduleName="Itinerary" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <NewItineraryModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveItinerary}
            />

            

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default Itineraries;

